import { css, styled } from 'styled-components';

import { device } from '@/styles/helpers';

interface SliderContainerProps {
  isFullScreen: boolean;
  paginationInset?: string;
}

export const ScSliderContainer = styled.div<SliderContainerProps>`
  position: relative;
  overflow: ${({ isFullScreen }) => (isFullScreen ? 'visible' : 'hidden')};
  z-index: 3;
  transition: height 0.3s ease-out;

  .pagination {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px 40px 28px;
    position: absolute;
    inset: ${({ paginationInset }) =>
      paginationInset ? paginationInset : 'auto auto 0 0'};
    width: 100%;
    z-index: 1;

    @media (${device.mobile}) {
      padding: 12px 40px 36px;
    }
  }

  .pagination-bullet {
    width: 100%;
    max-width: 48px;
    padding: 4px;
    cursor: pointer;

    &:before {
      position: relative;
      content: '';
      width: 100%;
      height: 4px;
      display: block;
      background: ${({ theme }) => theme.whiteColor};
      opacity: 0.2;
      border-radius: 20px;
      transition: 0.2s;
    }

    @media (${device.mobile}) {
      max-width: 80px;
    }

    &:hover {
      &:before {
        opacity: 0.3;
      }
    }

    &.active,
    &.active:hover {
      &:before {
        opacity: 1;
      }
    }
  }
`;

export interface ScArrowProps {
  hide?: boolean;
  isActive?: boolean;
  visibleForMob?: boolean;
  inset?: string;
}

const ScArrow = styled.button<ScArrowProps>`
  position: absolute;
  display: block;
  border: none;
  background: none;
  z-index: 2;
  width: 16px;
  height: 40px;
  top: 50%;
  transform: translateY(-50%) translateX(0);
  margin-top: -18px;
  cursor: pointer;
  transition: all 0.3s ease 0s;
  opacity: 1;

  ${({ visibleForMob }) =>
    !visibleForMob &&
    css`
      @media (max-width: 530px) {
        display: none;
      }
    `}
`;

export const ScArrowNext = styled(ScArrow)`
  inset: ${({ inset }) => inset ?? '50% 32px auto auto'};

  @media (hover: hover) {
    &:hover {
      &:before {
        transform: scale(1.2) rotate(-65deg);
        top: 34px;
      }
      &:after {
        transform: scale(1.2) rotate(65deg);
        top: 8px;
      }
    }
  }

  &:before {
    content: '';
    display: block;
    width: 24px;
    height: 1px;
    background-color: ${({ theme }) => theme.whiteColor};
    position: absolute;
    top: 30px;
    left: -4px;
    transform: rotate(-50deg);
    transition: all 0.3s ease 0s;
  }
  &:after {
    content: '';
    display: block;
    width: 24px;
    height: 1px;
    background-color: ${({ theme }) => theme.whiteColor};
    position: absolute;
    top: 12px;
    left: -4px;
    transform: rotate(50deg);
    transition: all 0.3s ease 0s;
  }
  ${({ hide }) =>
    hide &&
    css`
      transform: translateY(-50%) translateX(-32px);
      opacity: 0;
    `}
`;

export const ScArrowPrev = styled(ScArrow)`
  inset: ${({ inset }) => inset ?? '50% auto auto 32px'};

  @media (hover: hover) {
    &:hover {
      &:after {
        transform: scale(1.2) rotate(-65deg);
        top: 8px;
      }
      &:before {
        transform: scale(1.2) rotate(65deg);
        top: 34px;
      }
    }
  }

  &:before {
    content: '';
    display: block;
    width: 24px;
    height: 1px;
    background-color: ${({ theme }) => theme.whiteColor};
    position: absolute;
    top: 30px;
    left: -4px;
    transform: rotate(50deg);
    transition: all 0.3s ease 0s;
  }
  &:after {
    content: '';
    display: block;
    width: 24px;
    height: 1px;
    background-color: ${({ theme }) => theme.whiteColor};
    position: absolute;
    top: 12px;
    left: -4px;
    transform: rotate(-50deg);
    transition: all 0.3s ease 0s;
  }
  ${({ hide }) =>
    hide &&
    css`
      transform: translateY(-50%) translateX(32px);
      opacity: 0;
    `}
`;

export const ScOutsideArrowsContainer = styled.div<{
  visibleArrowsForMobile?: boolean;
}>`
  display: flex;
  justify-content: space-between;
  gap: 16px;

  & > div {
    display: flex;
    gap: 8px;
    flex-shrink: 0;

    & > a,
    & > button {
      flex-shrink: 0;
    }

    ${({ visibleArrowsForMobile }) =>
      visibleArrowsForMobile &&
      css`
        @media (${device.mobile}) {
          align-items: flex-end;
          padding-bottom: 24px;
        }
      `}
  }
`;

export const ScOutsideArrows = styled.div<{ dark?: boolean }>`
  height: 32px;
  padding: 4px 8px;
  background: ${({ theme, dark }) =>
    dark ? theme.blackColor : theme.grayLightColor};
  border-radius: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  .divider {
    height: 20px;
    width: 1px;
    background-color: ${({ theme }) => theme.whiteColor};
    opacity: 0.1;
  }
`;

const ScArrowOutside = styled.button<ScArrowProps>`
  display: block;
  border: none;
  background: none;
  z-index: 2;
  width: 100%;
  height: 100%;
  transition: all 0.3s ease 0s;
  opacity: 1;
  position: absolute;
`;

export const ScLeftArrowOutside = styled(ScArrowOutside)`
  position: relative;
  width: 24px;
  height: 24px;
  transition: 0.3s;
  transform: rotate(-180deg);

  cursor: ${({ isActive }) => (isActive ? 'pointer' : 'default')};
  opacity: ${({ isActive }) => (isActive ? 1 : 0.1)};

  ${({ isActive }) =>
    isActive &&
    css`
      @media (hover: hover) {
        &:hover {
          &:before {
            transform: rotate(-65deg);
            top: 16px;
          }
          &:after {
            transform: rotate(65deg);
            bottom: 16px;
          }
        }
      }
    `}

  &:before {
    content: '';
    display: block;
    width: 10px;
    height: 1px;
    background-color: ${({ theme }) => theme.whiteColor};
    position: absolute;
    top: 15px;
    left: 7px;
    transform: rotate(-50deg);
    transition: all 0.3s ease 0s;
  }
  &:after {
    content: '';
    display: block;
    width: 10px;
    height: 1px;
    background-color: ${({ theme }) => theme.whiteColor};
    position: absolute;
    bottom: 15px;
    left: 7px;
    transform: rotate(50deg);
    transition: all 0.3s ease 0s;
  }
`;

export const ScRightArrowOutside = styled(ScLeftArrowOutside)`
  transform: rotate(0);
`;

export interface ScSliderProps {
  slidesPerView?: number;
}

export const ScSlider = styled.div<ScSliderProps>`
  position: relative;
  overflow-x: scroll;
  display: flex;
  gap: 16px;
  scroll-snap-type: x mandatory;

  &::-webkit-scrollbar {
    display: none;
  }

  .snap-item {
    flex: ${({ slidesPerView }) =>
      `0 0 ${slidesPerView ? (100 * 100) / (slidesPerView * 100) : 100}%`};
    scroll-snap-align: start;
  }
`;
